<template>
  <b-container>
    <h1>Mercado Libre v2</h1>
    <hr>
    <div v-if="!isCompanyAdmin">
      <b-alert variant="warning" show>Esta integración la administra el usuario Company Admin de tu empresa.</b-alert>
    </div>
    <spinner v-else-if="loading" />
    <div v-else-if="error">
      <b-alert variant="danger" show>{{ error }}</b-alert>
      <b-button :to="{ name: 'company-integrations' }" variant="primary" size="sm">Volver a Integraciones</b-button>
    </div>
    <div v-else>
      <b-card class="mb-3">
        <h5 class="mb-3">Información útil sobre tu integración</h5>
        <b-row>
          <b-col md="6">
            <small class="text-muted d-block">USUARIO DE MERCADOLIBRE</small>
            <strong v-if="meliUser.nickname">{{ meliUser.nickname }}</strong>
            <span v-else class="text-muted">—</span>
            <small v-if="meliUser.id" class="text-muted d-block">ID {{ meliUser.id }}</small>
            <small v-if="meliUser.email" class="d-block"><em>({{ meliUser.email }})</em></small>
          </b-col>
          <b-col md="6">
            <small class="text-muted d-block">CORTE DE ENVÍOS</small>
            <strong>{{ formatCutTime }}</strong>
          </b-col>
        </b-row>
      </b-card>

      <b-card class="mb-3">
        <h5 class="mb-2">Configuración</h5>
        <p class="text-muted">Origen de los envíos y creación automática de tus ventas de Mercado Libre.</p>
        <b-row>
          <b-col md="6">
            <div class="form-group">
              <label class="control-label">Origen de los envíos (sucursal)</label>
              <b-form-select v-model="fromPlaceId">
                <template slot="first">
                  <option :value="null" disabled>Elegí una sucursal de origen</option>
                </template>
                <option v-for="store in availableStores" :key="store.id" :value="store.place_id">{{ store.reference }}</option>
              </b-form-select>
              <small class="form-text text-muted">Origen por defecto (para cuentas con un solo origen). Si tenés varios orígenes de Mercado Libre, asocialos abajo en "Orígenes de Mercado Libre".</small>
              <small class="form-text text-muted" v-if="availableStores.length === 0">
                No tenés sucursales con dirección validada.
                <b-link :to="{ name: 'stores' }">Crear una</b-link>.
              </small>
            </div>
            <div class="form-group">
              <b-form-checkbox v-model="automaticCreation" :value="true" :unchecked-value="false" switch :disabled="!fromPlaceId">
                Crear los envíos automáticamente (cada 30 min)
              </b-form-checkbox>
              <small class="form-text text-muted" v-if="!fromPlaceId">Necesitás elegir un origen antes de activar la creación automática.</small>
            </div>
            <b-button variant="primary" :disabled="saving" @click="save">{{ saving ? 'Guardando…' : 'Guardar cambios' }}</b-button>
          </b-col>
        </b-row>
      </b-card>

      <b-card class="mb-3">
        <h5 class="mb-2">Zonas FLEX</h5>
        <p class="text-muted">Activá o desactivá las zonas de Mercado Envíos Flex donde operás.</p>
        <spinner v-if="loadingFlex" />
        <b-alert variant="danger" show v-else-if="flexError">{{ flexError }}</b-alert>
        <template v-else>
          <p v-if="flexZones.length === 0" class="text-muted mb-0">No hay zonas Flex disponibles.</p>
          <template v-else>
            <b-form-checkbox-group v-model="flexSelected" stacked>
              <b-form-checkbox v-for="zone in flexZones" :key="zone.id" :value="zone.id">
                {{ zone.label || zone.id }}
              </b-form-checkbox>
            </b-form-checkbox-group>
            <b-button class="mt-2" variant="primary" size="sm" :disabled="savingFlex || !flexDirty" @click="saveFlexZones">
              {{ savingFlex ? 'Guardando…' : 'Guardar zonas' }}
            </b-button>
          </template>
          <div v-if="flexDeliveryRanges.length" class="mt-3">
            <small class="text-muted d-block">CORTES POR DÍA</small>
            <ul class="pl-3 mb-0">
              <li v-for="(r, i) in flexDeliveryRanges" :key="i">
                {{ r.name || r.id }}: {{ r.from || '—' }}{{ r.to ? ' – ' + r.to : '' }}
              </li>
            </ul>
          </div>
        </template>
      </b-card>

      <b-card class="mb-3">
        <h5 class="mb-2">Orígenes de Mercado Libre</h5>
        <p class="text-muted">Asociá cada origen de Mercado Libre a una sucursal Moova.</p>
        <spinner v-if="loadingOrigins" />
        <b-alert variant="danger" show v-else-if="originsError">{{ originsError }}</b-alert>
        <template v-else>
          <p v-if="origins.length === 0" class="text-muted mb-0">No hay orígenes de Mercado Libre.</p>
          <b-row v-for="o in origins" :key="o.id" class="mb-2 align-items-center">
            <b-col md="7">
              <strong>{{ formatOriginAddress(o.address) }}</strong>
              <small class="text-muted d-block">{{ o.meliOriginKey }}</small>
            </b-col>
            <b-col md="5">
              <b-form-select :value="o.moovaPlaceId" :disabled="savingOriginId === o.id" @change="associateOrigin(o, $event)">
                <template slot="first">
                  <option :value="null" disabled>Elegí una sucursal</option>
                </template>
                <option v-for="store in availableStores" :key="store.id" :value="store.place_id">{{ store.reference }}</option>
              </b-form-select>
              <small v-if="o.associated" class="text-success">Asociado ✓</small>
            </b-col>
          </b-row>
        </template>
      </b-card>

      <b-card class="mb-3">
        <h5 class="mb-3">Búsqueda rápida</h5>
        <b-row>
          <b-col md="8">
            <b-form @submit.prevent="searchOrders">
              <b-form-row>
                <b-col md="8">
                  <b-input
                    class="mb-2 col-12"
                    placeholder="Buscar por ID de venta, ID de ítem o alias del comprador, nombre o email"
                    v-model="q"
                  />
                  <label class="d-block mb-1">
                    Seleccioná el rango de fechas
                    <a v-show="dateRangeActive" href="#" class="ml-1" @click.prevent="resetDateRange">[reiniciar]</a>
                  </label>
                  <date-range-picker
                    v-if="locale.daysOfWeek"
                    v-model="dateRange"
                    @update="dateRangeActive = true; searchOrders()"
                    :locale-data="locale"
                    :opens="opens"
                    class="mb-2 w-100"
                    :ranges="ranges"
                    :min-date="minDate"
                  >
                    <div slot="input" slot-scope="picker" style="top: 3px; position: relative;">
                      <font-awesome-icon :icon="['far', 'calendar-alt']" size="1x" style="vertical-align: middle" />
                      <span style="vertical-align: middle">{{ moment(dateRange.startDate).format('L') }} - {{ moment(dateRange.endDate).format('L') }}</span>
                    </div>
                  </date-range-picker>
                  <b-form-row>
                    <b-col md="6" class="mb-2">
                      <b-form-select v-model="shippingStatus" size="sm" @change="searchOrders">
                        <option value="">Todos los estados</option>
                        <option value="ready_to_ship">Listo para enviar</option>
                        <option value="shipped">Enviado</option>
                        <option value="delivered">Entregado</option>
                        <option value="pending">Pendiente</option>
                        <option value="not_delivered">No entregado</option>
                        <option value="cancelled">Cancelado</option>
                      </b-form-select>
                    </b-col>
                    <b-col md="6" class="mb-2">
                      <b-input size="sm" v-model="buyerFilter" placeholder="Filtrar por comprador (alias exacto)" @keyup.enter="searchOrders" />
                    </b-col>
                  </b-form-row>
                </b-col>
                <b-col>
                  <b-button size="sm" type="submit" variant="primary">Buscar</b-button>
                  <a href="#" class="ml-3" @click.prevent="clearSearch">Borrar búsqueda</a>
                </b-col>
              </b-form-row>
            </b-form>
          </b-col>
          <b-col md="4" class="text-center align-self-center">
            <b-button variant="primary" @click="openCreateModal">Crear envío con ID de venta</b-button>
          </b-col>
        </b-row>
      </b-card>

      <b-modal id="modalCreateByOrderId" ref="modalCreateByOrderId" title="Crear envío con ID de venta" hide-footer @hide="resetCreateModal">
        <form v-if="!preview" @submit.stop.prevent="fetchPreview">
          <label for="ml-order-id">ID de venta</label>
          <b-form-input id="ml-order-id" v-model="createOrderId" placeholder="Ej. 2000012345678901" class="mb-3" />
          <b-alert variant="danger" :show="!!createError">{{ createError }}</b-alert>
          <div class="text-right">
            <b-button variant="primary" type="submit" :disabled="!createOrderId || previewing">
              {{ previewing ? 'Buscando…' : 'Buscar venta' }}
            </b-button>
          </div>
        </form>
        <div v-else>
          <dl class="row mb-2">
            <dt class="col-4">Venta</dt><dd class="col-8">{{ preview.orderId }}</dd>
            <dt class="col-4">Envío ML</dt><dd class="col-8">{{ preview.shippingId }}</dd>
            <dt class="col-4">Estado</dt><dd class="col-8">{{ preview.status || '—' }}</dd>
            <dt class="col-4">Comprador</dt><dd class="col-8">{{ (preview.buyer && (preview.buyer.name || preview.buyer.nickname)) || '—' }}</dd>
            <dt class="col-4">Destino</dt><dd class="col-8">{{ preview.destination || '—' }}</dd>
            <dt class="col-4">Ítems</dt>
            <dd class="col-8">
              <span v-if="!preview.items || preview.items.length === 0">—</span>
              <ul v-else class="pl-3 mb-0">
                <li v-for="(it, i) in preview.items" :key="i">{{ it.quantity }}× {{ it.title }}</li>
              </ul>
            </dd>
          </dl>
          <b-alert variant="warning" :show="preview.alreadyCreated">El envío para esta venta ya fue creado.</b-alert>
          <b-alert variant="danger" :show="!!createError">{{ createError }}</b-alert>
          <div class="text-right">
            <b-button variant="link" @click="preview = null; createError = null">Volver</b-button>
            <b-button variant="primary" :disabled="creating || preview.alreadyCreated" @click="confirmCreate">
              {{ creating ? 'Creando…' : 'Crear envío' }}
            </b-button>
          </div>
        </div>
      </b-modal>

      <div id="mercadolibre-list">
        <h5 class="mb-3">Tus ventas en MercadoLibre <b-link @click="searchOrders"><font-awesome-icon icon="sync-alt" /></b-link></h5>

        <spinner v-if="loadingOrders && orders.length === 0" />
        <b-alert variant="danger" show v-else-if="ordersError">{{ ordersError }}</b-alert>
        <b-tabs v-else>
          <b-tab title="Ventas simples" active>
            <p v-if="orders.length === 0" class="text-muted mt-3">No hay ventas para mostrar.</p>
            <template v-else>
              <mercadolibre-list-item v-for="order in orders" :key="order.orderId" :sale="mapSale(order)" />
              <div class="text-center mt-3">
                <b-button v-if="hasMore" variant="outline-primary" size="sm" :disabled="loadingOrders" @click="loadMore">
                  {{ loadingOrders ? 'Cargando…' : 'Buscar más' }}
                </b-button>
              </div>
            </template>
          </b-tab>
          <b-tab title="Ventas de carrito">
            <p class="text-muted mt-3">Sin ventas de carrito para mostrar.</p>
          </b-tab>
        </b-tabs>
      </div>
    </div>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex'
import DateRangePicker from 'vue2-daterange-picker'
import Spinner from '@/components/Spinner'
import MercadolibreListItem from '@/components/MercadolibreListItem'
import { FETCH_PROFILE } from '@/store/actions.type'

export default {
  name: 'MercadolibreV2Page',
  components: {
    Spinner,
    MercadolibreListItem,
    DateRangePicker
  },
  data() {
    return {
      platformSlug: 'meli-v2',
      loading: true,
      saving: false,
      error: null,
      application: null,
      fromPlaceId: null,
      automaticCreation: false,
      meliUser: {},
      orders: [],
      ordersTotal: 0,
      loadingOrders: false,
      ordersError: null,
      q: '',
      dateRange: {
        startDate: this.moment().subtract(6, 'day').startOf('day').toDate(),
        endDate: this.moment().endOf('day').toDate()
      },
      dateRangeActive: false,
      opens: 'right',
      locale: {},
      ranges: {},
      minDate: this.moment().subtract(30, 'day').startOf('day').toDate(),
      page: 1,
      shippingStatus: '',
      buyerFilter: '',
      flexZones: [],
      flexSelected: [],
      flexOriginal: [],
      flexDeliveryRanges: [],
      loadingFlex: false,
      savingFlex: false,
      flexError: null,
      origins: [],
      loadingOrigins: false,
      savingOriginId: null,
      originsError: null,
      createOrderId: '',
      preview: null,
      previewing: false,
      creating: false,
      createError: null
    }
  },
  computed: {
    isCompanyAdmin() {
      return this.$store.getters.profile && this.$store.getters.profile.company && this.$store.getters.profile.roles.findIndex(x => x.name === 'company-admin') !== -1
    },
    availableStores() {
      const stores = (this.user && this.user.company && this.user.company.stores) || []
      return stores.filter(x => x.place_id != null)
    },
    hasMore() {
      return this.orders.length < this.ordersTotal
    },
    formatCutTime() {
      const ct = this.cutTime
      if (!ct) return '—'
      const t = typeof ct === 'string' ? ct : ct.time
      return t ? String(t).slice(0, 5) : '—'
    },
    flexDirty() {
      return [...this.flexSelected].sort().join(',') !== [...this.flexOriginal].sort().join(',')
    },
    ...mapGetters([
      'user'
    ])
  },
  async mounted() {
    await this.$store.dispatch(FETCH_PROFILE)
    if (!this.isCompanyAdmin) {
      this.loading = false
      return
    }
    this.initDatePicker()
    await this.loadSettings()
    if (this.application) {
      this.fetchOrders(false)
      this.loadFlex()
      this.loadOrigins()
    }
  },
  methods: {
    async loadSettings() {
      this.loading = true
      this.error = null
      try {
        this.application = this.meliV2Application()
        if (!this.application) {
          this.error = 'La integración Mercado Libre v2 no está conectada.'
          return
        }
        const meli = this.$appConfig.integrations[this.platformSlug]
        const { data } = await this.$http.get(meli.baseUrl + 'v2/meli/settings?appId=' + this.application.id, {
          headers: { Auth: this.application.key, 'x-api-key': meli.header }
        })
        this.fromPlaceId = data.fromPlaceId != null ? data.fromPlaceId : null
        this.automaticCreation = Boolean(data.automaticCreation)
      } catch (e) {
        this.error = (e && e.body && e.body.message) || 'No pudimos leer la configuración de Mercado Libre v2.'
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (this.automaticCreation && !this.fromPlaceId) return
      this.saving = true
      try {
        const meli = this.$appConfig.integrations[this.platformSlug]
        const body = { automaticCreation: this.automaticCreation }
        if (this.fromPlaceId != null) body.fromPlaceId = Number(this.fromPlaceId)
        const { data } = await this.$http.patch(meli.baseUrl + 'v2/meli/settings?appId=' + this.application.id, body, {
          headers: { Auth: this.application.key, 'x-api-key': meli.header }
        })
        this.fromPlaceId = data.fromPlaceId != null ? data.fromPlaceId : null
        this.automaticCreation = Boolean(data.automaticCreation)
        this.$bvToast.toast(['Configuración guardada'], { title: this.$gettext('Notice'), variant: 'success', solid: true, autoHideDelay: 2500 })
      } catch (e) {
        const msg = (e && e.body && e.body.message) || 'No pudimos guardar la configuración.'
        this.$bvToast.toast([msg], { title: this.$gettext('Warning'), variant: 'danger', solid: true, autoHideDelay: 3500 })
      } finally {
        this.saving = false
      }
    },
    async fetchOrders(append = false) {
      if (!this.application) return
      this.loadingOrders = true
      this.ordersError = null
      try {
        const meli = this.$appConfig.integrations[this.platformSlug]
        const params = ['appId=' + encodeURIComponent(this.application.id), 'page=' + this.page]
        if (this.q) params.push('q=' + encodeURIComponent(this.q))
        if (this.dateRange.startDate) params.push('dateFrom=' + encodeURIComponent(this.moment(this.dateRange.startDate).startOf('day').toISOString()))
        if (this.dateRange.endDate) params.push('dateTo=' + encodeURIComponent(this.moment(this.dateRange.endDate).endOf('day').toISOString()))
        if (this.shippingStatus) params.push('shippingStatus=' + encodeURIComponent(this.shippingStatus))
        if (this.buyerFilter) params.push('buyer=' + encodeURIComponent(this.buyerFilter.trim()))
        const { data } = await this.$http.get(meli.baseUrl + 'v2/meli/orders?' + params.join('&'), {
          headers: { Auth: this.application.key, 'x-api-key': meli.header }
        })
        this.meliUser = { ...this.meliUser, ...(data.user || {}) }
        const results = data.results || []
        this.orders = append ? this.orders.concat(results) : results
        this.ordersTotal = (data.paging && data.paging.total) || this.orders.length
      } catch (e) {
        this.ordersError = (e && e.body && e.body.message) || 'No pudimos traer las ventas de Mercado Libre.'
      } finally {
        this.loadingOrders = false
      }
    },
    searchOrders() {
      this.page = 1
      this.orders = []
      this.fetchOrders(false)
    },
    initDatePicker() {
      this.locale = {
        direction: 'ltr',
        format: 'dd/mm/yyyy',
        separator: ' - ',
        applyLabel: 'Aplicar',
        cancelLabel: 'Cancelar',
        weekLabel: 'S',
        customRangeLabel: 'Personalizado',
        daysOfWeek: this.moment.weekdaysMin(),
        monthNames: this.moment.monthsShort(),
        firstDay: 1,
        showWeekNumbers: false
      }
      this.ranges = {
        Hoy: [this.moment().toDate(), this.moment().toDate()],
        Ayer: [this.moment().subtract(1, 'days').toDate(), this.moment().subtract(1, 'days').toDate()],
        'Últimos 7 días': [this.moment().subtract(6, 'days').toDate(), this.moment().toDate()],
        'Últimos 30 días': [this.moment().subtract(29, 'days').toDate(), this.moment().toDate()]
      }
    },
    resetDateRange() {
      this.dateRange = {
        startDate: this.moment().subtract(6, 'day').startOf('day').toDate(),
        endDate: this.moment().endOf('day').toDate()
      }
      this.dateRangeActive = false
      this.searchOrders()
    },
    clearSearch() {
      this.q = ''
      this.shippingStatus = ''
      this.buyerFilter = ''
      this.searchOrders()
    },
    async loadFlex() {
      if (!this.application) return
      this.loadingFlex = true
      this.flexError = null
      try {
        const meli = this.$appConfig.integrations[this.platformSlug]
        const headers = { Auth: this.application.key, 'x-api-key': meli.header }
        const qs = 'appId=' + encodeURIComponent(this.application.id)
        const [areasRes, settingsRes] = await Promise.all([
          this.$http.get(meli.baseUrl + 'v2/meli/flex/areas?' + qs, { headers }),
          this.$http.get(meli.baseUrl + 'v2/meli/flex/settings?' + qs, { headers })
        ])
        this.flexZones = areasRes.data || []
        this.flexSelected = this.flexZones.filter(z => z.selected).map(z => z.id)
        this.flexOriginal = [...this.flexSelected]
        this.flexDeliveryRanges = (settingsRes.data && settingsRes.data.deliveryRanges) || []
      } catch (e) {
        this.flexError = (e && e.body && e.body.message) || 'No pudimos traer las zonas Flex.'
      } finally {
        this.loadingFlex = false
      }
    },
    async saveFlexZones() {
      if (!this.application || !this.flexDirty) return
      this.savingFlex = true
      try {
        const meli = this.$appConfig.integrations[this.platformSlug]
        const headers = { Auth: this.application.key, 'x-api-key': meli.header }
        const qs = 'appId=' + encodeURIComponent(this.application.id)
        const toAdd = this.flexSelected.filter(id => this.flexOriginal.indexOf(id) === -1)
        const toRemove = this.flexOriginal.filter(id => this.flexSelected.indexOf(id) === -1)
        if (toAdd.length) {
          await this.$http.post(meli.baseUrl + 'v2/meli/flex/areas?' + qs, { zoneIds: toAdd }, { headers })
        }
        if (toRemove.length) {
          await this.$http.delete(meli.baseUrl + 'v2/meli/flex/areas?' + qs, { body: { zoneIds: toRemove }, headers })
        }
        this.$bvToast.toast(['Zonas actualizadas'], { title: this.$gettext('Notice'), variant: 'success', solid: true, autoHideDelay: 2500 })
        await this.loadFlex()
      } catch (e) {
        const msg = (e && e.body && e.body.message) || 'No pudimos actualizar las zonas.'
        this.$bvToast.toast([msg], { title: this.$gettext('Warning'), variant: 'danger', solid: true, autoHideDelay: 3500 })
      } finally {
        this.savingFlex = false
      }
    },
    formatOriginAddress(a) {
      if (!a) return '(sin dirección)'
      return a.addressLine || [a.street, a.city, a.state].filter(Boolean).join(', ') || '(sin dirección)'
    },
    async loadOrigins() {
      if (!this.application) return
      this.loadingOrigins = true
      this.originsError = null
      try {
        const meli = this.$appConfig.integrations[this.platformSlug]
        const headers = { Auth: this.application.key, 'x-api-key': meli.header }
        const { data } = await this.$http.get(meli.baseUrl + 'v2/meli/origins?appId=' + encodeURIComponent(this.application.id), { headers })
        this.origins = data || []
      } catch (e) {
        this.originsError = (e && e.body && e.body.message) || 'No pudimos traer los orígenes de Mercado Libre.'
      } finally {
        this.loadingOrigins = false
      }
    },
    async associateOrigin(origin, moovaPlaceId) {
      if (moovaPlaceId == null) return
      this.savingOriginId = origin.id
      try {
        const meli = this.$appConfig.integrations[this.platformSlug]
        const headers = { Auth: this.application.key, 'x-api-key': meli.header }
        const url = meli.baseUrl + 'v2/meli/origins/' + origin.id + '?appId=' + encodeURIComponent(this.application.id)
        const { data } = await this.$http.patch(url, { moovaPlaceId: Number(moovaPlaceId) }, { headers })
        origin.moovaPlaceId = data.moovaPlaceId
        origin.associated = data.moovaPlaceId != null
        origin.isManual = data.isManual
        this.$bvToast.toast(['Origen asociado'], { title: this.$gettext('Notice'), variant: 'success', solid: true, autoHideDelay: 2500 })
      } catch (e) {
        const msg = (e && e.body && e.body.message) || 'No pudimos asociar el origen.'
        this.$bvToast.toast([msg], { title: this.$gettext('Warning'), variant: 'danger', solid: true, autoHideDelay: 3500 })
      } finally {
        this.savingOriginId = null
      }
    },
    loadMore() {
      this.page += 1
      this.fetchOrders(true)
    },
    openCreateModal() {
      this.resetCreateModal()
      this.$refs.modalCreateByOrderId.show()
    },
    resetCreateModal() {
      this.createOrderId = ''
      this.preview = null
      this.previewing = false
      this.creating = false
      this.createError = null
    },
    async fetchPreview() {
      if (!this.createOrderId || !this.application) return
      this.previewing = true
      this.createError = null
      try {
        const meli = this.$appConfig.integrations[this.platformSlug]
        const orderId = encodeURIComponent(this.createOrderId.trim())
        const { data } = await this.$http.get(meli.baseUrl + 'v2/meli/orders/' + orderId + '/preview?appId=' + encodeURIComponent(this.application.id), {
          headers: { Auth: this.application.key, 'x-api-key': meli.header }
        })
        this.preview = data
      } catch (e) {
        this.createError = (e && e.body && e.body.message) || 'No pudimos encontrar la venta.'
      } finally {
        this.previewing = false
      }
    },
    async confirmCreate() {
      if (!this.preview || !this.application) return
      this.creating = true
      this.createError = null
      try {
        const meli = this.$appConfig.integrations[this.platformSlug]
        await this.$http.post(meli.baseUrl + 'v2/meli/orders?appId=' + encodeURIComponent(this.application.id), { orderId: String(this.preview.orderId) }, {
          headers: { Auth: this.application.key, 'x-api-key': meli.header }
        })
        this.$bvToast.toast(['Envío creado'], { title: this.$gettext('Notice'), variant: 'success', solid: true, autoHideDelay: 2500 })
        this.$refs.modalCreateByOrderId.hide()
        this.searchOrders()
      } catch (e) {
        this.createError = (e && e.body && e.body.message) || 'No pudimos crear el envío.'
      } finally {
        this.creating = false
      }
    },
    mapSale(o) {
      return {
        shipping_id: o.moovaShipmentId,
        fraud_risk_detected: false,
        external_code: o.shippingId,
        external_order_id: o.orderId,
        status: o.status,
        date_closed: o.dateClosed,
        to_contact: o.buyer && o.buyer.name,
        nickname: o.buyer && o.buyer.nickname,
        address_to: o.destination ? { formatted_address: o.destination } : null,
        to_extra: null,
        to_meta: null,
        shipping_items: (o.items || []).map(i => ({ description: i.title, quantity: i.quantity }))
      }
    }
  }
}
</script>

<style lang="scss">
#mercadolibre-list {
  .nav-tabs .nav-link.active {
    border: none !important;
    border-bottom: 3px solid #4fa0ff !important;
  }
}
</style>
